import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { fetchFranchises } from "../lib/franchises.js";
import { fetchPoolLots } from "../lib/lots.js";
import {
  lotsStatus,
  poolPagePath,
  seedingStatus,
  squadStatus,
  type PoolStep,
} from "../lib/organise.js";
import { fetchPoolSeeding } from "../lib/seeding.js";
import type { Tournament, TournamentEvent } from "../lib/types.js";

/**
 * The organiser's ways in to each pool's set-up, with where it stands, in one shape: the
 * tournament's Auction steps, the tournament-wide Seeding and Lots pages, and a pool's own
 * manage screen all use these rows. The fetches share their keys with the pool pages, so the
 * tap from a row to its page is served from cache.
 */
export function LinkRow({ to, title, facts }: { to: string; title: string; facts: string }) {
  return (
    <li className="border-b" style={{ borderColor: "var(--color-line-muted)" }}>
      <Link to={to} className="flex min-h-[56px] items-center gap-3 py-2">
        <span className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="text-base font-bold">{title}</span>
          {/* A space keeps the row's height while a status loads. */}
          <span className="font-mono text-[11px] text-ink-muted">{facts || " "}</span>
        </span>
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          aria-hidden
          className="shrink-0"
        >
          <path d="M9 5l7 7-7 7" />
        </svg>
      </Link>
    </li>
  );
}

export function useSquadSetup(tournament: Tournament) {
  return useQuery({
    queryKey: ["franchises", tournament.id],
    queryFn: () => fetchFranchises(tournament.id),
    enabled: tournament.auction,
  });
}

function SquadsRow({
  tournament,
  event,
  title,
}: {
  tournament: Tournament;
  event: TournamentEvent;
  title: string;
}) {
  const { data } = useSquadSetup(tournament);
  return (
    <LinkRow to={poolPagePath(event.id, "squads")} title={title} facts={squadStatus(data, event)} />
  );
}

function SeedingRow({ event, title }: { event: TournamentEvent; title: string }) {
  const closed = event.seedingClosedAt !== null;
  // A closed pool says so from the event alone; only an open one needs its panel.
  const { data } = useQuery({
    queryKey: ["pool-seeding", event.id],
    queryFn: () => fetchPoolSeeding(event.id),
    enabled: !closed,
  });
  return (
    <LinkRow
      to={poolPagePath(event.id, "seeding")}
      title={title}
      facts={seedingStatus(event, data)}
    />
  );
}

function LotsRow({ event, title }: { event: TournamentEvent; title: string }) {
  const settled = event.squadFill === "assigned" || event.lotsPublishedAt !== null;
  const { data } = useQuery({
    queryKey: ["pool-lots", event.id],
    queryFn: () => fetchPoolLots(event.id, event.entries),
    enabled: !settled,
  });
  return (
    <LinkRow to={poolPagePath(event.id, "lots")} title={title} facts={lotsStatus(event, data)} />
  );
}

/** One row per pool, opening that pool's page for the step. */
export function PoolRows({ tournament, step }: { tournament: Tournament; step: PoolStep }) {
  if (tournament.events.length === 0) {
    return <p className="mt-2 text-sm text-ink-muted">Add a pool first.</p>;
  }
  return (
    <ul>
      {tournament.events.map((event) =>
        step === "squads" ? (
          <SquadsRow key={event.id} tournament={tournament} event={event} title={event.name} />
        ) : step === "seeding" ? (
          <SeedingRow key={event.id} event={event} title={event.name} />
        ) : (
          <LotsRow key={event.id} event={event} title={event.name} />
        ),
      )}
    </ul>
  );
}

export function consoleStatus(tournament: Tournament, event: TournamentEvent): string {
  if (event.auctionFinishedAt !== null) return "Finished";
  return tournament.auctionStartedAt === null ? "Not started" : "Started";
}

/** A pool's own steps, for its manage screen: the same rows, named by step instead of pool. */
export function PoolStepRows({
  tournament,
  event,
}: {
  tournament: Tournament;
  event: TournamentEvent;
}) {
  return (
    <ul>
      <SquadsRow tournament={tournament} event={event} title="Squads" />
      <SeedingRow event={event} title="Seeding" />
      <LotsRow event={event} title="Lots" />
      <LinkRow
        to={`/tournaments/${tournament.id}/auction/${event.id}`}
        title="Auction console"
        facts={consoleStatus(tournament, event)}
      />
    </ul>
  );
}
